import React, { useState, useEffect } from 'react';
import { Holding } from '../../types/portfolio';
import { fetchHoldings } from '../../services/blockchainService';

interface HoldingsTableProps {
  userId: string;
  onBuy: (ticker: string) => void;
  onSell: (ticker: string) => void;
  refreshKey?: number;
}

function formatUSD(n: number) {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function HoldingsTable({ userId, onBuy, onSell, refreshKey }: HoldingsTableProps) {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchHoldings(userId);
        if (!cancelled) {
          setHoldings(data);
          setError('');
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load holdings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userId, refreshKey]);

  if (loading) return <div className="holdings-loading">Loading holdings…</div>;
  if (error) return <div className="holdings-error">{error}</div>;
  if (!holdings.length) {
    return (
      <div className="holdings-empty">
        No holdings yet. Use the <strong>Buy</strong> button to start trading.
      </div>
    );
  }

  return (
    <div className="holdings-table-wrap">
      <h3 className="section-title">Holdings</h3>
      <table className="holdings-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th>Name</th>
            <th>Shares</th>
            <th>Price</th>
            <th>Total Value</th>
            <th>Change %</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {holdings.map(h => (
            <tr key={h.ticker}>
              <td className="ticker-cell">{h.ticker}</td>
              <td>{h.name}</td>
              <td>{h.shares.toLocaleString('en-US', { maximumFractionDigits: 4 })}</td>
              <td>{formatUSD(h.currentPrice)}</td>
              <td>{formatUSD(h.totalValue)}</td>
              <td
                className={
                  h.changePercent >= 0 ? 'positive' : 'negative'
                }
              >
                {h.changePercent >= 0 ? '+' : ''}
                {h.changePercent.toFixed(2)}%
              </td>
              <td className="holding-actions">
                <button className="btn-buy-sm" onClick={() => onBuy(h.ticker)}>
                  Buy
                </button>
                <button className="btn-sell-sm" onClick={() => onSell(h.ticker)}>
                  Sell
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
